import React, { createContext, useCallback, useContext, useState } from 'react';

interface TransitionAttentionContextType {
  unseenCount: number;
  isUnseen: (id: string) => boolean;
  addUnseen: (id: string) => void;
  markSeen: (id: string) => void;
  clearAll: () => void;
}

const TransitionAttentionContext = createContext<TransitionAttentionContextType>({
  unseenCount: 0,
  isUnseen: () => false,
  addUnseen: () => {},
  markSeen: () => {},
  clearAll: () => {},
});

export const TransitionAttentionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [unseenIds, setUnseenIds] = useState<Set<string>>(new Set());

  const isUnseen = useCallback((id: string) => unseenIds.has(id), [unseenIds]);

  const addUnseen = useCallback((id: string) => {
    setUnseenIds((prev) => new Set([...prev, id]));
  }, []);

  const markSeen = useCallback((id: string) => {
    setUnseenIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setUnseenIds(new Set());
  }, []);

  return (
    <TransitionAttentionContext.Provider value={{ unseenCount: unseenIds.size, isUnseen, addUnseen, markSeen, clearAll }}>
      {children}
    </TransitionAttentionContext.Provider>
  );
};

export const useTransitionAttention = () => useContext(TransitionAttentionContext);
